import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import Link from 'libs/router/Link';

import Tooltip from 'components/Tooltip';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import Input from 'components/inputs/input/Input';

import UserDropdownItem from '../user-dropdown-item/UserDropdownItem';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { __t, applyTranslations } from 'utils/language';
import { getAgencyIdForOrganization } from 'utils/navigation';
import { getEntityFromOrganization, getEntityFromUser } from 'utils/view';
import { getNextFocusedItemIndex, resetFlags, updateFlags } from 'utils/user-dropdown';
import {
  ORGANIZATION_TYPE_BRAND,
  ORGANIZATION_TYPE_INFLUENCER,
  ORGANIZATION_TYPE_INFLUENCERS_AGENCY,
  getAgencyProfileId,
  getBrandProfileId,
  isCcmOnlyFocusedOrganization,
  isInfluencersFocusedOrganization
} from 'utils/organization';
import { getOrganizationData } from '../../../utils/metrics-data';

import './user-dropdown-list.scss';

import type {
  Account,
  Entity,
  Organization,
  TextEntry,
  UserDropdownAllowedKeys,
  UserDropdownOrganization
} from 'types';

import type { KeyboardEvent, SyntheticEvent } from 'react';

applyTranslations(require('./user-dropdown-list.i18n.json'));

const SETTINGS_LINK = '/user/settings';
const ORGANIZATION_TYPES = [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_INFLUENCER, ORGANIZATION_TYPE_INFLUENCERS_AGENCY];

type StateProps = {
  currentAccount: Account | undefined | null,
  currentOrganization: UserDropdownOrganization | undefined | null,
  mapByOrganizationId: {
    [x: number]: Organization
  }
};

type OwnProps = {
  organizations: Array<UserDropdownOrganization>,
  isOpen: boolean,
  onSelect: (b: string, a?: string | null) => void,
  onToggle: () => void
};

type Props = StateProps & OwnProps;

type State = {
  searchQuery: string,
  listItems: Array<UserDropdownOrganization>,
  focusedItem: UserDropdownOrganization | undefined | null,
  focusedItemIndex: number,
  ignoreKeyboard: boolean
};

const SHORT_LIST_LENGTH = 10;

class UserDropdownList extends React.Component<Props, State> {
  inputRef: HTMLTextAreaElement | HTMLInputElement | undefined | null;
  itemRef: React.RefObject<any> = { current: null };

  allowedKeys = {
    arrow: ['ArrowDown', 'ArrowUp'],
    selection: ['Enter']
  };

  state: State = {
    searchQuery: '',
    listItems: this.getSortedOrganizations(),
    focusedItem: null,
    focusedItemIndex: -1,
    ignoreKeyboard: false
  };

  componentDidUpdate(prevProps: Props) {
    const { isOpen, organizations } = this.props;

    if (this.inputRef && isOpen) {
      this.inputRef.focus();
    }

    if (organizations.length !== prevProps.organizations.length) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(prevState => ({
        ...prevState,
        listItems: this.getSortedOrganizations()
      }));
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { isOpen, organizations } = this.props;
    const { searchQuery, focusedItemIndex } = this.state;

    return isOpen !== nextProps.isOpen
      || searchQuery !== nextState.searchQuery
      || focusedItemIndex !== nextState.focusedItemIndex
      || organizations.length !== nextProps.organizations.length
      || document.activeElement !== this.inputRef;
  }

  getSortedOrganizations(): Array<UserDropdownOrganization> {
    // important to sort organization to be able later to calculate nextFocusedItemIndex even if organization are separated into 2 groups
    return sortBy(this.props.organizations, organization => isInfluencersFocusedOrganization(organization));
  }

  shouldShowSearch() {
    const organizations = this.getSortedOrganizations();
    return organizations.length > SHORT_LIST_LENGTH;
  }

  getFilteredItems(searchQuery: string): Array<UserDropdownOrganization> {
    const organizations = this.getSortedOrganizations();

    if (searchQuery.length === 0) {
      return organizations;
    }

    return organizations.filter(item => {
      const entity = item.entity;
      const displayName = (entity.displayName || '').trim().toLowerCase();

      return displayName.includes(searchQuery);
    });
  }

  handleResetState(): void {
    const organizations = this.getSortedOrganizations();

    this.setState({
      searchQuery: '',
      listItems: organizations,
      focusedItemIndex: -1,
      focusedItem: null
    });
  }

  handleSearchQueryChanged = (event: SyntheticEvent<TextEntry>): void => {
    if (!this.shouldShowSearch()) {
      return;
    }

    const searchQuery = (event.currentTarget.value || '').toLowerCase();

    this.setState({
      searchQuery,
      listItems: this.getFilteredItems(searchQuery),
      focusedItemIndex: -1
    });
  };

  handleSelect = (event: SyntheticEvent<any>): void => {
    const { onSelect } = this.props;
    const target = event.currentTarget;
    const orgId = target.dataset.arg;
    const href = target.href || null;

    this.handleResetState();

    onSelect(orgId, href);
  };

  handleKeyPress = (event: KeyboardEvent<TextEntry>): void => {
    const { ignoreKeyboard } = this.state;

    const keyPressed = event.key as UserDropdownAllowedKeys;

    const isArrowKey = this.allowedKeys.arrow.includes(keyPressed);
    const isSelectionKey = this.allowedKeys.selection.includes(keyPressed);

    if (ignoreKeyboard) {
      return;
    }

    if (isArrowKey) {
      this.handleItemFocus(keyPressed);
      return;
    }

    if (isSelectionKey) {
      this.handleSelectionKey();
    }
  };

  handleItemFocus(keyPressed: UserDropdownAllowedKeys): void {
    const { listItems, focusedItemIndex } = this.state;
    const nextFocusedItemIndex = getNextFocusedItemIndex(listItems.length, focusedItemIndex, keyPressed);
    const focusedItem = listItems.find((item, index) => index === nextFocusedItemIndex);

    if (focusedItem) {
      this.setState({
        listItems: updateFlags(listItems, focusedItem, 'isFocused'),
        focusedItemIndex: nextFocusedItemIndex,
        focusedItem
      }, () => this.handleScroll(focusedItem));
    }
  }

  handleScroll(focusedItem: UserDropdownOrganization): void {
    this.itemRef[focusedItem.id].current.scrollIntoView(false);
  }

  handleSelectionKey(): void {
    const { onSelect } = this.props;
    const { focusedItem } = this.state;

    this.handleResetState();

    if (focusedItem && this.itemRef[focusedItem.id] && this.itemRef[focusedItem.id].current) {
      const orgId = this.itemRef[focusedItem.id].current.dataset.arg;

      onSelect(orgId);
    }
  }

  handleAccountClick = (type: string): void => {
    const { onToggle, currentOrganization } = this.props;
    const isAgencyType = type === 'agency';

    this.handleResetState();

    onToggle();

    if (!currentOrganization) {
      return;
    }

    addUserAction({
      eventCategory: 'drop down',
      eventAction: 'navigate',
      stage: 'start',
      destination: isAgencyType ? 'agency-settings' : 'account-settings',
      customDimensionData: getOrganizationData(currentOrganization),
      eventLabel: {
        type: 'settings',
        entity: isAgencyType ? 'agency' : 'account'
      }
    });
  };

  handleMouseOver = (): void => {
    const { listItems, focusedItemIndex } = this.state;

    this.setState({
      ignoreKeyboard: true
    });

    if (focusedItemIndex === -1) {
      return;
    }

    const updatedListItems = resetFlags(listItems, 'isFocused');

    this.setState({
      listItems: updatedListItems,
      focusedItemIndex: -1,
      focusedItem: null
    });
  };

  handleMouseOut = (): void => {
    this.setState({
      ignoreKeyboard: false
    });
  };

  getBrandAgencyProfileId(id?: number | null) {
    const { mapByOrganizationId } = this.props;
    const agencyOrganization = id ? mapByOrganizationId[id] : null;

    return getBrandProfileId(agencyOrganization);
  }

  getInfluencerAgencyProfileId(id?: number | null) {
    const { mapByOrganizationId } = this.props;
    const agencyOrganization = id ? mapByOrganizationId[id] : null;

    return getAgencyProfileId(agencyOrganization);
  }

  renderListItems = (items: Array<UserDropdownOrganization>): Array<JSX.Element> => items.map((item) => {
    const entity = item.entity;
    const { currentAccount, currentOrganization } = this.props;

    const listItemClasses = classnames('user-dropdown-list__item', {
      'user-dropdown-list__item--highlighted': item.isFocused
    });

    this.itemRef[item.id] = React.createRef();

    return (
      <li
        key={`user-dropdown-list-item-${String(item.id)}-${String(entity.firstName)}-${String(entity.displayName)}`}
        ref={this.itemRef[item.id]}
        className={listItemClasses}
        data-qa="user-dropdown-list-item"
        data-arg={item.id}
        onMouseOver={this.handleMouseOver}
        onMouseOut={this.handleMouseOut}
      >
        <UserDropdownItem
          organization={item}
          onClick={this.handleSelect}
          currentAccount={currentAccount}
          currentOrganization={currentOrganization}
        />
      </li>
    );
  });

  renderEntity(entity: Entity | null, link: string, type: string): React.ReactNode {
    return (
      <div className="user-dropdown-list__account-info">
        <Link
          to={link}
          onClick={() => this.handleAccountClick(type)}
          className="user-dropdown-list__link"
        >
          <Tooltip
            message={__t(`user_dropdown_list.tooltip.${type}`)}
            width="auto-compact"
          >
            <EntityAvatar
              bordered
              isClickable
              entity={entity}
              customSize={{ width: 24, height: 24 }}
              className="user-dropdown-list__avatar"
            />
          </Tooltip>
        </Link>
        <div className="user-dropdown-list__account">
          <Tooltip
            message={__t(`user_dropdown_list.tooltip.${type}`)}
            width="auto-compact"
          >
            <Link
              to={link}
              onClick={() => this.handleAccountClick(type)}
              className="user-dropdown-list__link"
            >
              <EntityName
                entity={entity}
                size="inherit"
                className="user-dropdown-list__account-name"
              />
            </Link>
            <Link
              to={link}
              className="user-dropdown-list__account-link"
              onClick={() => this.handleAccountClick(type)}
            >
              {__t(`user_dropdown_list.edit_${type}`)}
            </Link>
          </Tooltip>
        </div>
      </div>
    );
  }

  renderAccountLink(): React.ReactNode {
    const { currentAccount } = this.props;
    const entity = getEntityFromUser(currentAccount);

    return this.renderEntity(entity, SETTINGS_LINK, 'account');
  }

  renderAgencyLink() {
    const { currentOrganization, mapByOrganizationId } = this.props;
    const agencyOrganizationId = getAgencyIdForOrganization(currentOrganization);
    const isCcmOnlyOrganization = isCcmOnlyFocusedOrganization(currentOrganization);

    if (!agencyOrganizationId || isCcmOnlyOrganization) {
      return null;
    }

    const brandProfileId = this.getBrandAgencyProfileId(agencyOrganizationId);
    const influencerAgencyProfileId = this.getInfluencerAgencyProfileId(agencyOrganizationId);

    if (!brandProfileId && !influencerAgencyProfileId) {
      return null;
    }

    const organization = mapByOrganizationId[agencyOrganizationId];
    const entity = getEntityFromOrganization(organization);

    if (influencerAgencyProfileId) {
      return this.renderEntity(entity, `/influencers-agencies/${influencerAgencyProfileId}/settings/profile/edit`, 'agency');
    }

    return this.renderEntity(entity, `/agency/${agencyOrganizationId}/overview`, 'agency');
  }

  renderSearchField(): React.ReactNode {
    const { searchQuery } = this.state;

    return (
      <div className="user-dropdown-list__search-field">
        <Input
          refCallback={(ref) => { this.inputRef = ref; }}
          value={searchQuery || ''}
          width="responsive"
          placeholder={__t('user_dropdown_list.user_dropdown_search_placeholder')}
          className="user-dropdown-list__input"
          onChange={this.handleSearchQueryChanged}
        />
      </div>
    );
  }

  renderOrganizations() {
    const { listItems } = this.state;
    const groupedItems = groupBy(listItems, item => { // eslint-disable-line arrow-body-style
      switch (item.type) {
        case ORGANIZATION_TYPE_INFLUENCER:
          return ORGANIZATION_TYPE_INFLUENCER;
        case ORGANIZATION_TYPE_INFLUENCERS_AGENCY:
          return ORGANIZATION_TYPE_INFLUENCERS_AGENCY;
        default:
          return ORGANIZATION_TYPE_BRAND;
      }
    });

    const showHeaders = Object.keys(groupedItems).length > 1;

    return ORGANIZATION_TYPES.map<React.ReactNode>((key: string) => {
      if (!groupedItems[key]) {
        return null;
      }

      return (
        <React.Fragment key={key}>
          {
            showHeaders && (
              <div className="user-dropdown-list__group-name">
                {__t(`user_dropdown_list.groups.${key}`)}
              </div>
            )
          }
          {this.renderListItems(groupedItems[key])}
        </React.Fragment>
      );
    });
  }

  render() {
    const showSearch = this.shouldShowSearch();
    const searchClasses = classnames('user-dropdown-list__search', {
      'user-dropdown-list__search--hidden': !showSearch
    });

    return (
      <section
        className="user-dropdown-list"
        data-qa="user-dropdown-list"
        onKeyDown={this.handleKeyPress}
      >
        {this.renderAccountLink()}
        {this.renderAgencyLink()}
        <div className={searchClasses}>
          {this.renderSearchField()}
        </div>
        <ul className="user-dropdown-list__items">
          {this.renderOrganizations()}
        </ul>
      </section>
    );
  }
}

const mapStateToProps = ({ account, organizations }): StateProps => ({
  currentAccount: account.currentAccount,
  currentOrganization: organizations.currentOrganization,
  mapByOrganizationId: organizations.mapByOrganizationId
});

export default connect(mapStateToProps)(UserDropdownList);
